import type { CSSProperties } from 'react';
import { SignInButton } from './SignInButton.js';

interface AuthUser {
  displayName: string;
}

interface AuthBarProps {
  isAuthenticated: boolean;
  user: AuthUser | null;
  logout: () => void;
  primaryColor: string;
  textColor: string;
}

/**
 * Auth bar — shows signed-in user info (avatar + name + sign-out button)
 * or sign-in buttons (Google / Facebook) when anonymous.
 */
export function AuthBar({ isAuthenticated, user, logout, primaryColor, textColor }: AuthBarProps) {
  const signedInBarStyle: CSSProperties = {
    width: 'min(100%, 600px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.06)',
    fontSize: '0.95rem',
  };

  if (isAuthenticated && user) {
    return (
      <div style={signedInBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: `${primaryColor}44`,
              border: `2px solid ${primaryColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: primaryColor,
              flexShrink: 0,
            }}
          >
            {user.displayName.slice(0, 1).toUpperCase()}
          </div>
          <span style={{ fontWeight: 600 }}>{user.displayName}</span>
        </div>
        <button
          onClick={logout}
          style={{
            padding: '8px 14px',
            fontSize: '0.85rem',
            fontWeight: 600,
            border: '1.5px solid rgba(255,255,255,0.2)',
            borderRadius: '8px',
            background: 'transparent',
            color: textColor,
            cursor: 'pointer',
            touchAction: 'manipulation',
            opacity: 0.7,
          }}
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: 'min(100%, 600px)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ fontSize: '0.9rem', opacity: 0.65, textAlign: 'center' }}>
        Sign in to unlock all scoring modes and track your stats
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <SignInButton provider="google" style={{ flex: 1 }} />
        <SignInButton provider="facebook" style={{ flex: 1 }} />
      </div>
    </div>
  );
}
